import { useState, useEffect } from 'react';
import { useNavigationManager, APP_VERSION } from '../../../core';
import { useAuth } from '../../../contexts/AuthContext';
import { RevoltIcon, AppLikeButton, RevoltMagicButton } from '../../../components';
import { RevoltButton, RevoltButtonLink } from '../../../components/RevoltButton';
import { HeaderAccountPanel, HeaderAccountPanelMobileTop, HeaderAccountPanelMobileCredits } from '../../../components/HeaderAccountPanel';
import { canShowRemixButton, canShowWorkspaceButton } from '../../../core/utils/appAccessUtils';
import { envUtils } from '../../../core/utils';
import './SharingHeader.css';

export function SharingHeader({
  app,
  appId,
  viewTotal = 0,
  appManager,
  onLikeToggle,
  onRefresh,
  onFullscreen,
  onRemix,
  onOpenWorkspace,
  onOpenSettings,
  isOwner,
  isAdmin,
  refreshDisabled
}) {
  const navigationManager = useNavigationManager();
  const { user, membershipDetails, isAuthenticated, isSupporter, logout, storedAccounts, switchToAccount, removeAccount } = useAuth();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const uiMode = envUtils.device.getRecommendedUIMode();
    return uiMode === 'mobile';
  });

  useEffect(() => {
    const check = () => {
      const uiMode = envUtils.device.getRecommendedUIMode();
      setIsMobile(uiMode === 'mobile');
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleLogoClick = async () => {
    await navigationManager.goHome();
  };

  const handleAccountMenuClick = async () => {
    await navigationManager.navigateTo('account');
  };

  const handleAdminClick = async () => {
    await navigationManager.navigateTo('admin');
  };

  const handleAuthClick = async () => {
    await navigationManager.requireAuth(window.location.pathname + window.location.search);
  };

  const handleLogout = async () => {
    await navigationManager.goHome({
      onComplete: async () => {
        await logout();
      }
    });
  };

  const handleAccountSwitch = async (accountId) => {
    try {
      const result = await switchToAccount(accountId);
      if (!result.success) {
        console.error('Failed to switch account:', result.error);
      }
      return result;
    } catch (error) {
      console.error('Account switch error:', error);
      return { success: false, error };
    }
  };

  const handleRemoveAccount = (accountId, event) => {
    event.stopPropagation();
    removeAccount(accountId);
  };

  const getMembershipPlan = () => {
    try {
      const membership = user?.membership;
      if (membership && typeof membership === 'object' && membership.planId) {
        return membership.planId;
      }
      return '';
    } catch (error) {
      console.error('Error getting membership plan:', error);
      return '';
    }
  };

  const isFreePlan = () => getMembershipPlan() === 'free';
  const isMaxPlan = () => getMembershipPlan() === 'max';

  const appTitle = app?.name || 'Untitled App';
  const appOwner = app?.ownerDisplayName || app?.ownerUsername || 'Unknown owner';

  // Remix logic for sharing mode - owner/admin always see it; others only if allowRemixing
  const canRemix = canShowRemixButton(app, { isOwner, isAdmin });
  const canShowWorkspace = canShowWorkspaceButton({ isOwner, isAdmin });

  return (
    <header className="sharing-header">
      {/* Top row */}
      <div className={`sharing-header-top ${isAuthenticated && isMobile ? 'sharing-header-top--mobile' : ''}`}>
        <div className="sharing-header-left">
          <a href="/main" className="header-logo-container" data-navigation-button onClick={(e) => { e.preventDefault(); handleLogoClick(); }}>
            <img
              src="https://bluedrake42.nyc3.cdn.digitaloceanspaces.com/revolt-site/site-assets/logo/revolt-logo-small-transparent.png"
              alt="Revolt"
              className="header-logo-img"
            />
            <span className="header-logo-text">RevoltGPT.dev</span>
          </a>

          <div className="sharing-version">
            {APP_VERSION}
          </div>
        </div>

        <div className="sharing-header-right">
        {isAuthenticated ? (
          <>
            {/* Mobile: badges + avatar (row 1) */}
            {isMobile && (
              <div className="sharing-header-account-mobile-top">
                <HeaderAccountPanelMobileTop
                  user={user}
                  isMaxPlan={isMaxPlan()}
                  isSupporter={isSupporter}
                  storedAccounts={storedAccounts}
                  onAccountMenuClick={handleAccountMenuClick}
                  onAdminClick={handleAdminClick}
                  onLogout={handleLogout}
                  onAccountSwitch={handleAccountSwitch}
                  onRemoveAccount={handleRemoveAccount}
                />
              </div>
            )}

            {/* Mobile: credits (row 2) */}
            {isMobile && (
              <div className="sharing-header-account-mobile-credits">
                <HeaderAccountPanelMobileCredits membershipDetails={membershipDetails} />
              </div>
            )}

            {/* Desktop: Upgrade + unified panel */}
            {!isMobile && (
              <>
                {isFreePlan() && (
                  <RevoltButtonLink href="/pricing" data-navigation-button onClick={(e) => { e.preventDefault(); navigationManager.navigateTo('pricing'); }}>
                    <i className="fa-solid fa-arrow-up"></i> Upgrade
                  </RevoltButtonLink>
                )}
                <HeaderAccountPanel
                  user={user}
                  membershipDetails={membershipDetails}
                  isMaxPlan={isMaxPlan()}
                  isSupporter={isSupporter}
                  storedAccounts={storedAccounts}
                  onAccountMenuClick={handleAccountMenuClick}
                  onAdminClick={handleAdminClick}
                  onLogout={handleLogout}
                  onAccountSwitch={handleAccountSwitch}
                  onRemoveAccount={handleRemoveAccount}
                />
              </>
            )}
          </>
        ) : (
          <div className="auth-buttons">
            <RevoltButtonLink href="#auth" onClick={handleAuthClick}>
              Sign In / Sign Up
            </RevoltButtonLink>
          </div>
        )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="sharing-header-bottom">
        <div className="sharing-app-info">
          <div className="sharing-app-title">{appTitle}</div>
          <div className="sharing-app-owner">by {appOwner}</div>
          <div className="sharing-app-stats">
            <div className="sharing-stat">
              <RevoltIcon name="eye" size="sm" />
              <span>{viewTotal}</span>
            </div>
            <AppLikeButton
              appId={appId}
              initialLikes={app?.likes || 0}
              initialLiked={app?.userLiked || false}
              manager={appManager}
              onToggle={onLikeToggle}
            />
          </div>
        </div>

        <div className="sharing-app-actions">
          {canRemix && (
            <RevoltMagicButton
              onClick={onRemix}
            >
              <RevoltIcon name="lucide:wand-sparkles" size="sm" />
              Remix
            </RevoltMagicButton>
          )}
          {canShowWorkspace && onOpenSettings && (
            <RevoltButton
              className="playspace-button"
              as="a"
              href={`/app-settings/${appId}`}
              data-navigation-button
              onClick={async (e) => {
                e.preventDefault();
                await onOpenSettings();
              }}
            >
              <i className="fa-solid fa-gear"></i> Settings
            </RevoltButton>
          )}
          {canShowWorkspace && onOpenWorkspace && (
            <RevoltButton
              className="playspace-button"
              as="a"
              href={`/workspace/${appId}`}
              data-navigation-button
              onClick={async (e) => {
                e.preventDefault();
                await onOpenWorkspace();
              }}
            >
              <i className="fa-solid fa-code"></i> Workspace
            </RevoltButton>
          )}
          <RevoltButton
            className="playspace-button"
            onClick={onRefresh}
            disabled={refreshDisabled}
          >
            <i className="fa-solid fa-rotate-right"></i> Refresh
          </RevoltButton>
          <RevoltButton
            className="playspace-button"
            onClick={onFullscreen}
          >
            <i className="fa-solid fa-expand"></i> Full Screen
          </RevoltButton>
        </div>
      </div>
    </header>
  );
}